import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import client from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const { finishAuth } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const r = await client.post('/auth/reset-password', { token, password });
      finishAuth(r.data);
      toast.success('Password updated — you are signed in');
      navigate('/dashboard');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Reset failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="relative flex min-h-[100dvh] items-start justify-center overflow-y-auto bg-[hsl(var(--foreground))] p-4 sm:items-center">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(1000px_circle_at_20%_0%,rgba(245,179,1,0.18),transparent_55%)]" />
      <div className="relative z-10 my-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="mb-6 flex justify-center"><Logo /></div>
        {!token ? (
          <div className="text-center">
            <p className="text-sm text-[hsl(var(--danger))]">This reset link is invalid or incomplete.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/login')} data-testid="reset-back-button">Back to login</Button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-[hsl(var(--primary))]/10 p-3 text-sm text-[hsl(var(--primary))]"><ShieldCheck className="h-4 w-4" />Choose a new password</div>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>New password</Label>
                <div className="relative mt-1">
                  <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="reset-password-input" required minLength={6} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="reset-password-toggle">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                </div>
              </div>
              <div><Label>Confirm password</Label><Input type={showPw ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" data-testid="reset-confirm-input" required /></div>
              <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="reset-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update password</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
